import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Popover from '@mui/material/Popover';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import {
  IconButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import ColumnsIcon from '@grnhse/seedling/lib/birch/icons/columns';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { withLocalePrefix } from 'shared/utils/translation';
import { useResizeObserver } from 'shared/utils/useResizeObserver';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.header_row');
const tFilterRow = withLocalePrefix('talent_matching.filter_row');

const QUESTION_COLUMN_PREFIX = 'qa_';

type ColumnOption = {
  field: string;
  label: string;
};

type Props = {
  columns: ColumnOption[];
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
};

const StyledPopover = styled(Popover)`
  && {
    z-index: 150;
  }

  .MuiPopover-paper {
    overflow: visible;
  }
`;

const ColumnSelectorPanel = styled.div`
  border: 1px solid ${FullPalette.GREY_600};
  border-radius: 5px;
  box-shadow: 0px 3px 11px 0px ${FullPalette.EVERGREEN_E200};
  background-color: ${FullPalette.WHITE};
  width: 320px;
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: ${Tokens.spacing[2]};
  padding-bottom: ${Tokens.spacing['0.5']};
`;

const PanelContent = styled.div`
  padding: ${Tokens.spacing['0.5']} ${Tokens.spacing[2]} ${Tokens.spacing[2]};
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  overflow-x: hidden;
`;

const PanelFooter = styled.div`
  padding: ${Tokens.spacing[2]};
  padding-top: ${Tokens.spacing[1]};
  border-top: 1px solid ${FullPalette.GREY_600};
  display: flex;
  justify-content: flex-end;
`;

const TruncatedLabel = styled.span`
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  max-width: 100%;
  word-break: break-word;
`;

const QuestionLabel = ({ label }: { label: string }) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const updateIsTruncated = useCallback(() => {
    const el = labelRef.current;
    if (!el) {
      return;
    }

    // With 1-line clamping, vertical overflow reliably indicates truncation.
    setIsTruncated(el.scrollHeight > el.clientHeight);
  }, []);

  useEffect(() => {
    updateIsTruncated();
  }, [label, updateIsTruncated]);

  useResizeObserver(updateIsTruncated, labelRef);

  const labelContent = <TruncatedLabel ref={labelRef}>{label}</TruncatedLabel>;

  if (!isTruncated) {
    return labelContent;
  }

  return (
    <Tooltip ariaLabel={label} label={label}>
      {labelContent}
    </Tooltip>
  );
};

const CustomColumnSelector = ({
  columns,
  columnVisibilityModel,
  setColumnVisibilityModel,
  defaultColumnVisibilityModel,
}: Props) => {
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, open, close] = useModal();

  const [columnOptions, questionOptions] = useMemo(() => {
    const nextColumnOptions: ColumnOption[] = [];
    const nextQuestionOptions: ColumnOption[] = [];

    columns.forEach((col) => {
      if (col.field.startsWith(QUESTION_COLUMN_PREFIX)) {
        nextQuestionOptions.push(col);
      } else {
        nextColumnOptions.push(col);
      }
    });

    return [nextColumnOptions, nextQuestionOptions];
  }, [columns]);

  return (
    <div ref={anchorElRef}>
      <IconButton
        label={t('buttons.show_hide_columns')}
        icon={ColumnsIcon}
        onClick={open}
      />
      {anchorElRef.current && (
        <StyledPopover
          open={isOpen}
          onClose={close}
          anchorEl={anchorElRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableScrollLock={false}
        >
          <ColumnSelectorPanel>
            <PanelHeader>
              <Stack direction="row" justifyContent="space-between">
                <Body medium>{t('buttons.show_hide_columns')}</Body>
                <IconButton
                  label={tFilterRow('close')}
                  onClick={close}
                  icon={CloseIcon}
                  size="md"
                />
              </Stack>
            </PanelHeader>
            <PanelContent>
              <Stack spacing={2}>
                <Stack spacing={1}>
                  {columnOptions.map((col) => (
                    <Checkbox
                      key={col.field}
                      label={col.label}
                      value={col.field}
                      checked={columnVisibilityModel[col.field] !== false}
                      onChange={(checked) => {
                        setColumnVisibilityModel({
                          ...columnVisibilityModel,
                          [col.field]: checked,
                        });
                      }}
                    />
                  ))}
                </Stack>

                {questionOptions.length > 0 && (
                  <Stack spacing={1}>
                    <Body medium>{t('column_selector.questions_heading')}</Body>
                    <Stack spacing={1}>
                      {questionOptions.map((col) => (
                        <Checkbox
                          key={col.field}
                          label={col.label}
                          CustomLabel={QuestionLabel}
                          value={col.field}
                          checked={columnVisibilityModel[col.field] !== false}
                          onChange={(checked) => {
                            setColumnVisibilityModel({
                              ...columnVisibilityModel,
                              [col.field]: checked,
                            });
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </PanelContent>
            {defaultColumnVisibilityModel && (
              <PanelFooter>
                <TertiaryButton
                  onClick={() =>
                    setColumnVisibilityModel(defaultColumnVisibilityModel)
                  }
                >
                  {t('buttons.reset_columns')}
                </TertiaryButton>
              </PanelFooter>
            )}
          </ColumnSelectorPanel>
        </StyledPopover>
      )}
    </div>
  );
};

export default CustomColumnSelector;
